import { of } from 'rxjs';
import { Component, OnInit, Inject, HostListener, Output, EventEmitter  } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormArray, FormControl } from '@angular/forms';
import { Session } from 'src/app/model/session';
import { User } from 'src/app/model/user';
import { AdminService } from 'src/app/service/admin.service';
import { UserEventAnswer } from 'src/app/enum/user.event.answer';
import { UserEventRole } from 'src/app/enum/user.event.role';
import { Submission, SubmissionStatus } from 'src/app/model/paper';
import { EventsService } from 'src/app/service/events.service';
import { SelectOption } from 'src/app/model/select.option';
import { SubmissionsService } from 'src/app/service/submissions.service';
import { NotificationService } from 'src/app/service/notification.service';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ESCAPE } from '@angular/cdk/keycodes';
import { NoopScrollStrategy } from '@angular/cdk/overlay';
import { filter } from 'rxjs/operators';
import { ConfirmationDialogComponent } from 'src/app/component/confirmation-dialog/confirmation-dialog.component';
import { AddSessionSubmissionsDialogComponent } from 'src/app/component/add-session-submissions-dialog/add-session-submissions-dialog.component';
import { SelectableSubmission } from 'src/app/component/session-submissions-picker/session-submissions-picker.component';
import { PaperTitlePipe } from 'src/app/pipe/paper.title.pipe';
import { UserAffiliationPipe } from 'src/app/pipe/user-affiliation.pipe';
import { TimezoneService } from 'src/app/service/timezone.service';
import { CdkDragDrop, moveItemInArray, CDK_DRAG_CONFIG } from '@angular/cdk/drag-drop';
import { DEFAULT_SESSION_COLOR } from 'src/app/lib/session-color';

export interface DialogData {
  params?: {
    action?: any;
    content?: any;
    session?: any;
    saveSession?: any;
    showAlocated?: any;
    eventTimeZone?: string;
    /** Abre o diálogo já "sujo" — usado ao CRIAR uma sessão (ver `hasChanges`). */
    startDirty?: boolean;
  };
  cancel: string;
  confirm: string;
}

@Component({
  selector: 'app-general-session-dialog',
  templateUrl: './general-session-dialog.component.html',
  styleUrls: ['./general-session-dialog.component.scss'],
  providers: [
    // O CDK escreve o `z-index` do preview de arraste INLINE (default 1000), o
    // que vence qualquer regra CSS nossa. Como este arraste roda dentro de um
    // mat-dialog — cujo `.cdk-overlay-container` é forçado a 2010 —, o card
    // arrastado ficava ATRÁS do diálogo: invisível até ser solto. Este é o
    // ponto previsto pela lib para ajustar o valor.
    { provide: CDK_DRAG_CONFIG, useValue: { zIndex: 2100 } }
  ]
})
export class GeneralSessionDialogComponent implements OnInit {
  dialogMessages: DialogData = {
    cancel: 'forms.buttons.discard',
    confirm: 'forms.buttons.save',
    params: {}
  };

  session: Session;
  showAlocated: boolean;
  @Output() changeAlocated = new EventEmitter<boolean>();
  allSubmissions: Array<SelectableSubmission>;
  currentSessionSubmissions: Array<Submission>;
  finalSubmissions: Array<Number>;
  form: FormGroup; 
  searchFoundUsers: User;
  validityDate: boolean = true;

  /**
   * Marcação de submissões alterada. As caixas da aba "Submissões" usam
   * `ngModel` em cada objeto, fora do FormGroup — então `form.dirty` não as
   * enxerga e, sem esta flag, alocar/desalocar não habilitaria os botões.
   */
  submissionsTouched = false;


  /**
   * Título da sessão por id — alimenta a coluna "Sessão" da lista. As
   * submissões trazem apenas o id da sessão em que estão alocadas.
   */
  sessionTitleById = new Map<number, string>();

  /**
   * Submissões DESTA sessão, na ordem de apresentação (aba "Submissões V2").
   *
   * É derivada da marcação da aba "Submissões", mas guardada como lista PRÓPRIA
   * porque carrega algo que a outra não tem: a ORDEM. Um getter recalculado a
   * cada ciclo perderia o arrasto (o array mudaria de identidade no meio do
   * gesto) e a ordem definida à mão.
   */
  sessionSubmissions: SelectableSubmission[] = [];


  /** Confirmação de descarte aberta (evita empilhar outra ao repetir Esc). */
  private confirmingDiscard = false;

  private eventTimeZone: string;

  /**
   * Chair já gravado, no formato que o `app-user-search` espera em
   * `[includeUsers]` (ele busca cada usuário por `id`).
   *
   * O `chair` que chega aqui não tem forma única: vem como `User` quando a
   * sessão foi lida do servidor, mas o calendário o reduz ao id ao salvar
   * (`eventSession.chair = eventSession.chair?.id ?? null`), e é `null` em
   * sessão nova. Sem normalizar, um id ausente virava `GET /core/user/null/`.
   */
  chairUsers: User[] = [];

  /** Estado inicial dos campos, para detectar alteração (ver `hasChanges`). */
  private initialSnapshot: string;
  /** Id do coordenador já gravado na sessão (ou null). */
  private initialChairId: number = null;
  /** Abre já "sujo" (ver `hasChanges`) — sessão nova, ainda não gravada. */
  private startDirty = false;


    constructor(
      private adminService: AdminService,
      private fb: FormBuilder,
      public notificationService: NotificationService,
      private submissionsService: SubmissionsService,
      private eventService: EventsService,
      public dialogRef: MatDialogRef<GeneralSessionDialogComponent>,
      private tzService: TimezoneService,
      private userAffiliationPipe: UserAffiliationPipe,
      private dialog: MatDialog,
      @Inject(MAT_DIALOG_DATA) public data: DialogData
    ) {
      if (data) {
        Object.assign(this.dialogMessages, data);
      }

      // Esc = mesmo caminho do "x". O diálogo abre com `disableClose`, então o
      // Material não fecha sozinho — escutamos a tecla e decidimos aqui.
      this.dialogRef.keydownEvents()
        .pipe(filter(event => event.keyCode === ESCAPE))
        .subscribe(() => this.onEscape());
    }

    /**
     * Esc só chega ao diálogo quando não há nada sobreposto para fechar antes:
     * um popover (globo de fusos, menus) ou o autocomplete da busca de pessoa
     * têm precedência — Esc ali deve fechá-los, não o diálogo inteiro. Também
     * ignora enquanto a confirmação de descarte está aberta, para não empilhar
     * uma segunda.
     */
    private onEscape(): void {
      const hasOverlayOnTop = !!document.querySelector('.jems-popover-panel, .mat-autocomplete-panel');
      if (hasOverlayOnTop || this.confirmingDiscard) { return; }
      this.dismiss();
    }

    /**
     * Há algo para salvar ou descartar?
     *
     * NÃO usa `form.dirty`: vários caminhos deste formulário escrevem por
     * `setValue` (o datepicker confirma o valor no overlay, o seletor de cor
     * espelha o hexadecimal, o `app-user-search` repõe a lista), e `setValue`
     * não marca o controle como sujo. A comparação é com o estado inicial.
     */
    get hasChanges(): boolean {
      if (!this.form) { return false; }
      // `startDirty`: sessão que ainda NÃO existe no servidor (criada pelo card
      // fantasma de Listar Programa). Fechar sem salvar perde tudo, então o
      // diálogo nasce sujo — "Salvar" habilitado e "Descartar" pedindo
      // confirmação, como em qualquer edição pendente.
      if (this.startDirty) { return true; }
      return this.snapshot() !== this.initialSnapshot
          || this.chairChanged
          || this.submissionsTouched;
    }

    /**
     * Retrato dos campos comparáveis por valor. Fora dele ficam `searchChair`
     * (o componente de busca mexe nesse controle por conta própria, inclusive
     * ao carregar o chair já gravado) e `chair`/`submissions`, que só são
     * preenchidos na hora de salvar — os dois têm verificação própria.
     */
    private snapshot(): string {
      const value = { ...this.form.getRawValue() };
      delete value.searchChair;
      delete value.chair;
      delete value.submissions;
      return JSON.stringify(value);
    }

    /** Coordenador trocado (ou removido) em relação ao que estava gravado. */
    private get chairChanged(): boolean {
      const currentId = this.selectedChair ? this.selectedChair.id : null;
      return (currentId ?? null) !== (this.initialChairId ?? null);
    }

    /**
     * Título do modal: "Título - Subtítulo". Sem subtítulo, só o título (o
     * hífen solto ficaria pendurado). Lê os MESMOS controles dos campos, então
     * acompanha a digitação sem estado paralelo.
     */
    get modalTitle(): string {
      return this.sessionSubtitle
        ? `${this.sessionTitle} - ${this.sessionSubtitle}`
        : this.sessionTitle;
    }

    /** Título da sessão (parte forte do cabeçalho). */
    get sessionTitle(): string {
      return this.form ? (this.form.get('title').value || '').trim() : '';
    }

    /** Subtítulo (parte atenuada do cabeçalho). */
    get sessionSubtitle(): string {
      return this.form ? (this.form.get('subtitle').value || '').trim() : '';
    }

    /**
     * Rótulo do botão da esquerda: sem alteração pendente ele apenas fecha, e
     * chamá-lo de "Descartar" prometeria algo que não acontece.
     */
    get dismissLabel(): string {
      return this.hasChanges ? this.dialogMessages.cancel : 'forms.buttons.close';
    }

    /**
     * Saída do diálogo (botão "x" e botão da esquerda). Com alteração pendente,
     * confirma o descarte antes de fechar — o MESMO diálogo do
     * `UnsavedChangesGuard`, para a experiência ser a de sempre.
     */
    dismiss(): void {
      if (!this.hasChanges) {
        this.dialogRef.close();
        return;
      }

      this.confirmingDiscard = true;
      this.dialog.open(ConfirmationDialogComponent, {
        // Idem: diálogo aninhado não deve reaplicar o bloqueio de rolagem.
        scrollStrategy: new NoopScrollStrategy(),
        data: {
          title: 'unsaved-changes-dialog.title',
          content: 'unsaved-changes-dialog.content',
          cancel: 'forms.buttons.cancel',
          confirm: 'forms.buttons.discard-and-leave',
          destructive: true
        }
      }).afterClosed().subscribe(confirmed => {
        this.confirmingDiscard = false;
        if (confirmed === true) { this.dialogRef.close(); }
      });
    }

    /**
     * Grava e fecha. Antes o fechamento vinha do `[mat-dialog-close]` na
     * diretiva do botão Material; com botão nativo, é explícito aqui.
     */
    saveAndClose(): void {
      if (!this.canSave) { return; }
      this.save();
      this.dialogRef.close(true);
    }

    /** Salvar só é permitido com mudança pendente E dados válidos. */
    get canSave(): boolean {
      return this.hasChanges && !!this.form && this.form.valid && this.validityDate;
    }

    @HostListener('window:keyup.Enter', ['$event'])
    onDialogClick(event: KeyboardEvent): void {
      // Enter respeita as mesmas condições do botão Salvar — antes gravava
      // mesmo sem alteração e mesmo com data inválida.
      if (!this.canSave) { return; }
      this.save();
      this.dialogRef.close();
    }
    

    ngOnInit(): void {
      setTimeout(() => {
        this.showAlocated = this.dialogMessages.params.showAlocated;
        this.session = this.dialogMessages.params.session;
        this.eventTimeZone = this.dialogMessages.params.eventTimeZone;
        this.startDirty = !!this.dialogMessages.params.startDirty;
        this.chairUsers = this.resolveChairUsers();
        this.initialChairId = this.chairUsers.length ? this.chairUsers[0].id : null;
        this.getSubmissions();
        this.loadSessionTitles();
        this.initForm();
      });
    }

    hexToRgb(hex) {
      const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
      hex = hex.replace(shorthandRegex, (m, r, g, b) => {
        return r + r + g + g + b + b;
      });
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : null;
    }

    /** Normaliza `session.chair` (User | id | null) para o `[includeUsers]`. */
    private resolveChairUsers(): User[] {
      const chair: any = this.session?.chair;
      if (!chair) { return []; }

      const id = typeof chair === 'object' ? chair.id : chair;
      return id ? [{ id } as User] : [];
    }

    private initForm(): void {
      const rgb = this.hexToRgb(this.session.backgroundColor ? this.session.backgroundColor : DEFAULT_SESSION_COLOR);
      const chair = this.session?.chair;
      this.form = this.fb.group({
        id: [this.session.id, []],
        submissions: [this.session.submissions],
        event: [this.session.event, [Validators.required]],
        title: [this.session.title, [Validators.maxLength(100)]],
        subtitle: [this.session.subtitle, [Validators.maxLength(100)]],
        url: [this.session.url, [Validators.maxLength(100)]],
        room: [this.session.room, [Validators.maxLength(100)]],
        start: [this.session.start, [Validators.required]],
        timeZoneStart: [this.tzService.browserTimeZone],
        end: [this.session.end, [Validators.required]],
        timeZoneEnd: [this.tzService.browserTimeZone],
        // `chair` é o que vai para o backend (objeto User ou null); quem o
        // preenche é o `searchChair`, na hora de salvar.
        chair: [chair ?? null, []],
        // O `app-user-search` zera este controle no próprio init; quem repõe o
        // chair da sessão é o `[includeUsers]` no template.
        searchChair: [[], []],
        backgroundColor: [this.session.backgroundColor ? this.session.backgroundColor : DEFAULT_SESSION_COLOR, []]
      });
      this.checkValidityFormDate();
      this.initialSnapshot = this.snapshot();
    }

    private checkValidityFormDate() {
      this.form.get('end').valueChanges.subscribe(() => {
        this.validityDate = this.validityDateForm();
      });
      this.form.get('start').valueChanges.subscribe(() => {
        this.validityDate = this.validityDateForm();
      });
      this.form.controls.start.markAsTouched();
      this.form.controls.end.markAsTouched();
    }

    private validityDateForm(): boolean {
      const isValid: boolean = this.form.get('start').value < this.form.get('end').value;

      isValid ? this.form.get('end').setErrors(null) : this.form.get('end').setErrors({ 'dateEndAfterStart': true });
      isValid ? this.form.get('start').setErrors(null) : this.form.get('start').setErrors({ 'dateEndAfterStart': true });

      return isValid;
    }

    /**
     * Chair escolhido no `app-user-search` (que trabalha com lista, mesmo em
     * `singleUserSearch`). Adicionar e remover acontecem dentro do componente;
     * aqui só lemos o resultado.
     */
    private get selectedChair(): User {
      const selected: User[] = this.form.get('searchChair').value;
      return selected && selected.length ? selected[0] : null;
    }

    /**
     * Reconcilia a lista ordenada com a marcação da aba "Submissões":
     * mantém a ordem atual, acrescenta no fim as recém-marcadas e remove as
     * desmarcadas. Chamada ao carregar e a cada clique de checkbox.
     */
    syncSessionSubmissions(): void {
      const selected = (this.allSubmissions || []).filter(item => item.selected);
      const kept = this.sessionSubmissions.filter(item => selected.indexOf(item) >= 0);
      const added = selected.filter(item => kept.indexOf(item) < 0);
      this.sessionSubmissions = [...kept, ...added];
    }

    /** Marcação alterada no picker (aba "Submissões" ou diálogo de adicionar). */
    onSubmissionSelectionChange(): void {
      this.submissionsTouched = true;
      this.syncSessionSubmissions();
    }

    /**
     * Card fantasma da aba "Submissões V2": abre o diálogo de adicionar, que
     * mostra o MESMO conteúdo da aba "Submissões" mais Descartar/Salvar.
     * A seleção é escrita na mesma lista; o diálogo se encarrega de restaurar
     * o estado anterior quando descartado.
     */
    openAddSubmissions(): void {
      this.dialog.open(AddSessionSubmissionsDialogComponent, {
        width: '625px',
        height: '450px',
        maxHeight: '85vh',
        panelClass: ['my-custom-dialog-class', 'session-dialog-panel'],
        disableClose: true,
        autoFocus: false,
        // Diálogo ANINHADO: a rolagem da página já está bloqueada por este
        // diálogo. Aplicar o bloqueio de novo faz o CDK guardar como "posição
        // anterior" um deslocamento que já está zerado e reescrever `left`/`top`
        // no <html> — a página aparecia empurrada, com uma faixa branca à
        // esquerda. `NoopScrollStrategy` deixa o bloqueio a cargo do diálogo de
        // fora, que o desfaz corretamente ao fechar.
        scrollStrategy: new NoopScrollStrategy(),
        data: {
          submissions: this.allSubmissions,
          sessionId: this.session.id,
          sessionTitleById: this.sessionTitleById,
        }
      }).afterClosed().subscribe(saved => {
        if (saved) { this.onSubmissionSelectionChange(); }
      });
    }

    /** Arrasto na aba "Submissões V2": reordena e marca o diálogo como sujo. */
    dropSubmission(event: CdkDragDrop<SelectableSubmission[]>): void {
      if (event.previousIndex === event.currentIndex) { return; }
      moveItemInArray(this.sessionSubmissions, event.previousIndex, event.currentIndex);
      this.submissionsTouched = true;
    }

    /**
     * Remove a submissão desta sessão (x do card), com confirmação. Desmarcar
     * é o que a lista da aba entende como "não faz mais parte" — o objeto
     * continua na coleção geral, disponível para ser adicionado de novo.
     */
    removeSubmission(submission: SelectableSubmission): void {
      this.dialog.open(ConfirmationDialogComponent, {
        // Rolagem já bloqueada pelo diálogo de sessão (ver `openAddSubmissions`).
        scrollStrategy: new NoopScrollStrategy(),
        data: {
          title: 'admin.session-dialog.subs-remove-title',
          content: 'admin.session-dialog.subs-remove-content',
          params: { content: { title: submission.title } },
          confirm: 'forms.buttons.remove',
          destructive: true
        }
      }).afterClosed().subscribe(confirmed => {
        if (!confirmed) { return; }
        submission.selected = false;
        this.onSubmissionSelectionChange();
      });
    }

    /** Autores formatados: "Nome (SIGLA, País)", separados por ponto e vírgula. */
    authorsOf(submission: SelectableSubmission): string {
      const authors = (submission && submission.authors) || [];
      return authors
        .map(author => {
          const affiliation = this.userAffiliationPipe.transform(author.user);
          return affiliation ? `${author.user.name} ${affiliation}` : author.user.name;
        })
        .join('; ');
    }

    /** Cores em uso no evento, para a linha de recentes do seletor de cor. */
    usedColors: string[] = [];

    /** Carrega os títulos das sessões do evento para a coluna "Sessão". */
    private loadSessionTitles(): void {
      this.eventService.getSessionsByEvent(this.session.event).subscribe(sessions => {
        this.sessionTitleById = new Map(sessions.map(s => [s.id, s.title] as [number, string]));
        // Cores JÁ usadas no evento, da sessão mais recente para a mais antiga
        // (id decrescente). Alimentam a linha de recentes do seletor de cor —
        // que assim mostra o que o evento realmente usa, e não o que alguém
        // experimentou e descartou.
        this.usedColors = [...sessions]
          .sort((a, b) => b.id - a.id)
          .map(s => s.backgroundColor)
          .filter(color => !!color);
      });
    }

    /** Submissões do evento ainda estão sendo baixadas?
     *
     *  Trava o card "Adicionar submissão". Não é só cosmético: o diálogo de
     *  adicionar recebe `this.allSubmissions` por REFERÊNCIA no momento da
     *  abertura, e a carga abaixo SUBSTITUI a referência. Abrir antes de ela
     *  chegar deixava o diálogo preso na lista vazia — e ela não se preenchia
     *  mais, nem depois que os dados chegavam (issue #2381). */
    loadingSubmissions = false;

    private getSubmissions(): void {
      let hasFound:Boolean;
      this.allSubmissions = [];
      this.loadingSubmissions = true;

      // Sessão NOVA (ainda sem `id`): não existe "submissões desta sessão" para
      // buscar. Sem esta guarda o diálogo chamava `bySession/null`, que responde
      // 404 — e o tratamento global de erro leva à página de erro, tirando o
      // usuário da área do chair.
      const currentOfSession = this.session.id
        ? this.submissionsService.getSubmissionsBySession(this.session.id)
        : of([]);

      currentOfSession.subscribe(submissions => {
        this.currentSessionSubmissions = submissions;

        this.submissionsService.getAllSubmissionsByEvent(this.session.event).subscribe(submissions => {
          this.allSubmissions = submissions as SelectableSubmission[];

          for(var index in this.allSubmissions){
            hasFound = false;
            for(var sIndex in this.currentSessionSubmissions){
              if(!hasFound){
                if(this.currentSessionSubmissions[sIndex].id === this.allSubmissions[index].id){
                  this.allSubmissions[index].selected = true;
                  hasFound = true;
                }else{
                  this.allSubmissions[index].selected = false;
                }
              }
            }
          }
          this.syncSessionSubmissions();
          this.loadingSubmissions = false;
        }, () => {
          // Sem ramo de erro, uma falha deixava o card travado para sempre.
          this.loadingSubmissions = false;
        });
      }, () => {
        this.loadingSubmissions = false;
      });
    }

    save() {
      // Ordem de apresentação definida na aba "Submissões V2" — por isso a
      // lista sai de `sessionSubmissions`, e não da varredura de `allSubmissions`.
      this.finalSubmissions = this.sessionSubmissions.map(item => item.id);
      this.form.patchValue({
        submissions : this.finalSubmissions,
      });
      
      this.form.get('chair').setValue(this.selectedChair);
      this.dialogMessages.params.saveSession(this.form.value);
    }

}
